import { useEffect, useRef, useState } from "react";
import { api, API_URL } from "@/lib/api";
import { Sparkles, Send, RotateCcw, X, Zap, Check, Loader2, Paperclip } from "lucide-react";

const newSession = () => {
  const sid = `ai-${crypto.randomUUID()}`;
  localStorage.setItem("crm_ai_session", sid);
  return sid;
};

export const parseAiMessage = (content) => {
  const raw = (content || "").replace(/\[\[status:[^\]]*(\]\]|$)/g, "");
  const m = raw.match(/```action\s*([\s\S]*?)```/);
  const text = raw.replace(/```action[\s\S]*?(```|$)/, "").trim();
  let action = null;
  if (m) { try { action = JSON.parse(m[1]); } catch { action = null; } }
  return { text, action };
};

export const lastAiStatus = (content) => {
  const all = [...(content || "").matchAll(/\[\[status:([^\]]*)\]\]/g)];
  return all.length ? all[all.length - 1][1] : "";
};

export const AiAssistant = ({ open, onClose }) => {
  const [session, setSession] = useState(() => localStorage.getItem("crm_ai_session") || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actBusy, setActBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [att, setAtt] = useState([]);
  const [upBusy, setUpBusy] = useState(false);
  const endRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files?.length || upBusy) return;
    setUpBusy(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/ai/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAtt((a) => [...a, r.data]);
        setMessages((m) => [...m, { role: "assistant",
          content: `📎 Read "${r.data.filename}" — ${r.data.analysis?.summary || "attached"}. Tell me what to do with it (e.g. "attach this bill against SO/26/0028").` }]);
      } catch (e) {
        setMessages((m) => [...m, { role: "assistant", content: `⚠️ Upload failed for ${f.name}: ${e.response?.data?.detail || e.message}` }]);
      }
    }
    setUpBusy(false);
  };

  useEffect(() => {
    if (open) api.get(`/ai/history/${session}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [open, session]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim() || (att.length ? "Process the attached file(s) and propose the right action." : "");
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    const fileIds = att.map((a) => a.file_id);
    const shownText = fileIds.length ? `${text}\n${att.map((a) => `📎 ${a.filename}`).join("\n")}` : text;
    setAtt([]);
    setMessages((m) => [...m, { role: "user", content: shownText }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_URL}/ai/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: text, file_ids: fileIds }),
      });
      if (!res.ok || !res.body) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: `Sorry — ${e.message}` };
        return copy;
      });
    }
    setBusy(false);
  };

  const runAction = async (action) => {
    if (actBusy) return;
    setActBusy(true);
    try {
      const r = await api.post("/ai/execute", { session_id: session, action: action.action, params: action.params || {} });
      setMessages((m) => [...m, { role: "assistant", content: r.data.message }]);
      if (r.data.download_url) {
        const b = await api.get(r.data.download_url, { responseType: "blob" });
        const url = URL.createObjectURL(b.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = r.data.filename || "statement.pdf";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠️ ${e.response?.data?.detail || e.message}` }]);
    }
    setActBusy(false);
  };

  const reset = () => { setSession(newSession()); setMessages([]); setDismissed(new Set()); };

  if (!open) return null;
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[90vw] max-w-[330px] h-[440px] max-h-[70vh] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden" data-testid="ai-assistant-panel">
        <div className="flex items-center justify-between px-4 py-3 bg-[#3B82F6] text-white">
          <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles size={15} className="text-[#FC8F8F]" /> AI Assistant</div>
          <div className="flex items-center gap-1">
            <button onClick={reset} className="p-1.5 rounded hover:bg-white/10" title="New chat" data-testid="ai-new-chat"><RotateCcw size={14} /></button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="ai-close"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
          {messages.length === 0 && (
            <div className="text-xs text-slate-500 space-y-2 mt-4">
              <p className="font-semibold text-slate-600">Try asking:</p>
              {["Which follow-ups are pending or overdue?", "Who owes us money right now?",
                "Do we have 8 x 12 tube in stock?", "Add 50 meters of ID 8mm x OD 12mm tube to stock"].map((q) => (
                <button key={q} onClick={() => setInput(q)} className="block w-full text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#3B82F6]/40 transition-colors duration-200">{q}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => {
            const { text, action } = m.role === "assistant" ? parseAiMessage(m.content) : { text: m.content, action: null };
            const isLast = i === messages.length - 1;
            return (
              <div key={i} className="space-y-2">
                {(text || !action) && (
                  <div className={`max-w-[88%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "ml-auto bg-[#3B82F6] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                    {text || (busy && isLast ? (
                      <span className="flex flex-col gap-1" data-testid="ai-typing-indicator">
                        <span className="inline-flex items-center gap-1 py-0.5 text-slate-400">
                          <span className="ai-typing-dot" />
                          <span className="ai-typing-dot" style={{ animationDelay: "0.18s" }} />
                          <span className="ai-typing-dot" style={{ animationDelay: "0.36s" }} />
                        </span>
                        {lastAiStatus(m.content) && (
                          <span className="text-[11px] text-slate-400 italic" data-testid="ai-tool-status">{lastAiStatus(m.content)}</span>
                        )}
                      </span>
                    ) : "")}
                  </div>
                )}
                {action && (isLast && !busy && !dismissed.has(i) ? (
                  <div className="max-w-[88%] rounded-xl border border-[#3B82F6]/25 bg-white p-3 shadow-sm" data-testid="ai-action-card">
                    <div className="flex items-start gap-2 text-[13px] text-slate-800">
                      <Zap size={14} className="text-[#FC5F5F] mt-0.5 flex-none" />
                      <span className="font-medium">{action.summary || "Run this action?"}</span>
                    </div>
                    <div className="flex gap-2 mt-2.5">
                      <button onClick={() => runAction(action)} disabled={actBusy} data-testid="ai-action-confirm"
                              className="flex-1 h-8 rounded-full bg-[#3B82F6] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:bg-[#2563EB] transition-colors duration-200">
                        {actBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirm
                      </button>
                      <button onClick={() => setDismissed((s) => new Set(s).add(i))} disabled={actBusy} data-testid="ai-action-cancel"
                              className="px-4 h-8 rounded-full border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 transition-colors duration-200">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-400 px-1" data-testid="ai-action-chip">
                    <Zap size={11} className="flex-none" /> {action.summary}
                  </div>
                ))}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-slate-200">
          {att.length > 0 && (
            <div className="space-y-1 mb-2" data-testid="ai-attachment-chips">
              {att.map((a, i) => (
                <div key={a.file_id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-[11px] text-blue-800" data-testid="ai-attachment-chip">
                  <Paperclip size={11} className="flex-none" />
                  <span className="truncate flex-1">{a.filename}</span>
                  <button onClick={() => setAtt((arr) => arr.filter((_, j) => j !== i))} className="p-0.5 hover:text-blue-950" data-testid="ai-attachment-remove"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="file" accept=".pdf,image/*" multiple className="hidden" id="ai-file-input"
                   onChange={(e) => { uploadFiles([...(e.target.files || [])]); e.target.value = ""; }} data-testid="ai-file-input" />
            <button onClick={() => document.getElementById("ai-file-input").click()} disabled={upBusy}
                    className="w-10 h-10 flex-none rounded-full border border-slate-200 text-slate-500 flex items-center justify-center hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors duration-200 disabled:opacity-40"
                    title="Attach bills / POs (PDFs or photos — select multiple)" data-testid="ai-attach-btn">
              {upBusy ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
            </button>
            <input value={input} onChange={(e) => setInput(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && send()}
                   placeholder="Ask or tell me to do something…"
                   className="flex-1 min-w-0 text-sm border border-slate-200 rounded-full px-4 py-2.5 outline-none focus:border-[#3B82F6]"
                   data-testid="ai-input" />
            <button onClick={send} disabled={busy || (!input.trim() && !att.length)}
                    className="w-10 h-10 flex-none rounded-full bg-[#3B82F6] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#2563EB] transition-colors duration-200"
                    data-testid="ai-send"><Send size={15} /></button>
          </div>
        </div>
    </div>
  );
};
